/**
 * Item library — the catalog of all reviewable items in the curriculum.
 *
 * An ITEM is the smallest unit of FSRS scheduling. Each item gets its own
 * card (stability + difficulty) and is reviewed independently. For ear training,
 * "P5 in the major-context" and "M3 in the major-context" are separate items
 * because users will master them at different rates.
 *
 * Items are derived from CURRICULUM NODES. A single node like
 * "diatonic-intervals-major" expands into many items — one per interval.
 *
 * Item IDs are stable strings so they survive across app updates.
 */

export type Track =
  | "pitch"
  | "modes"
  | "intervals"
  | "harmony"
  | "dictation"
  | "counterpoint"
  | "guitar";

/**
 * Tracks tied to a specific instrument rather than instrument-agnostic ear
 * training. These are **opt-in**: a node on one of these tracks is dormant —
 * absent from the daily plan, drill hub, radar, and placement — until the user
 * enables its track via the `enabledInstrumentTracks` preference. Gate the
 * curriculum through `visibleNodes` / `visibleItems` so dormant content never
 * leaks into scheduling. See docs/design/guitar-harmony-v1.md §5.1.
 */
export const INSTRUMENT_TRACKS: ReadonlySet<Track> = new Set<Track>(["guitar"]);

/** Whether a track is gated behind an opt-in `enabledInstrumentTracks` entry. */
export function isInstrumentTrack(track: Track): boolean {
  return INSTRUMENT_TRACKS.has(track);
}

/** Whether a track should be live for a user with the given opt-ins. */
export function isTrackEnabled(track: Track, enabledInstrumentTracks: readonly Track[]): boolean {
  return !isInstrumentTrack(track) || enabledInstrumentTracks.includes(track);
}

/**
 * Curriculum phase — the pedagogical rung a node sits on, independent of its
 * track. Drives the Learn index's vertical ordering (Foundation → Advanced).
 * Authoritative table in CURRICULUM.md §2; `phase.test.ts` keeps the two honest.
 */
export type Phase = "1" | "2A" | "2B+" | "3";

export interface CurriculumNode {
  id: string;
  track: Track;
  title: string;
  phase: Phase;
  prerequisites: string[];
}

export interface Item {
  /** Stable ID, e.g. "diatonic-degrees-major::5". */
  id: string;
  /** The curriculum node this item belongs to. */
  nodeId: string;
  /** Human-readable label for UI display, e.g. "scale degree 5 (sol) in major". */
  label: string;
  /** Parameters the exercise generator uses to render this item. */
  params: Record<string, string | number>;
  /**
   * The exercise type that uses this item. Routes to the right exercise generator.
   * Tracks roughly map to the curriculum tree in the previous step.
   */
  exerciseType:
    | "scale-degree-recognition"
    | "interval-recognition"
    | "chord-quality-recognition"
    | "chord-inversion-recognition"
    | "progression-recognition"
    | "cadence-recognition"
    | "mode-recognition"
    | "slash-chord-recognition"
    | "secondary-dominant-recognition"
    | "modulation-recognition"
    | "melodic-dictation"
    | "rhythmic-dictation"
    | "fret-interval-recognition"
    | "fret-triad-recognition"
    | "fret-triad-inversion-recognition"
    | "fret-caged-recognition"
    | "fret-caged-anchoring"
    | "fret-diatonic-recognition"
    | "fret-diatonic-minor-recognition"
    | "fret-seventh-recognition"
    | "fret-scale-degree-recognition"
    | "fret-scale-degree-caged-recognition";
}

/**
 * The MVP node set. This is a subset of the full curriculum tree
 * — just enough nodes to demonstrate the system end-to-end.
 * In the real app this list grows to ~50 nodes.
 */
export const MVP_NODES: CurriculumNode[] = [
  {
    id: "diatonic-degrees-major",
    track: "pitch",
    title: "Diatonic scale degrees (major)",
    phase: "1",
    prerequisites: [],
  },
  {
    id: "diatonic-degrees-minor",
    track: "pitch",
    title: "Diatonic scale degrees (minor, do-based)",
    phase: "1",
    prerequisites: ["diatonic-degrees-major"],
  },
  {
    id: "chromatic-degrees-major",
    track: "pitch",
    title: "Chromatic scale degrees (major)",
    phase: "1",
    prerequisites: ["diatonic-degrees-major"],
  },
  {
    id: "diatonic-intervals-major",
    track: "intervals",
    title: "Diatonic intervals in key context",
    phase: "1",
    prerequisites: ["diatonic-degrees-major"],
  },
  {
    id: "chromatic-intervals",
    track: "intervals",
    title: "Chromatic intervals (tritone, ♭6, ♭7)",
    phase: "1",
    prerequisites: ["diatonic-intervals-major", "chromatic-degrees-major"],
  },
  {
    id: "triad-quality",
    track: "harmony",
    title: "Triad quality (maj/min/dim/aug)",
    phase: "1",
    prerequisites: ["diatonic-intervals-major"],
  },
  {
    id: "triad-inversions",
    track: "harmony",
    title: "Triad inversions",
    phase: "1",
    prerequisites: ["triad-quality"],
  },
  {
    id: "seventh-chord-quality",
    track: "harmony",
    title: "Seventh chord quality",
    phase: "1",
    prerequisites: ["triad-quality"],
  },
  {
    id: "diatonic-progressions",
    track: "harmony",
    title: "Common diatonic progressions",
    phase: "1",
    prerequisites: ["triad-quality"],
  },
  {
    id: "cadence-recognition",
    track: "harmony",
    title: "Cadence recognition (PAC, plagal, half, deceptive)",
    phase: "1",
    prerequisites: ["diatonic-progressions"],
  },
  {
    id: "extended-chords-9",
    track: "harmony",
    title: "9th chords (maj9, min9, dom9)",
    phase: "2A",
    prerequisites: ["seventh-chord-quality"],
  },
  {
    id: "extended-chords-11-13",
    track: "harmony",
    title: "11th and 13th chords (maj11, min11, dom13)",
    phase: "2A",
    prerequisites: ["extended-chords-9"],
  },
  {
    id: "altered-dominants",
    track: "harmony",
    title: "Altered dominants (7♭9, 7♯9, 7♯11, 7♭13)",
    phase: "2A",
    prerequisites: ["extended-chords-9", "extended-chords-11-13"],
  },
  {
    id: "slash-chords",
    track: "harmony",
    title: "Slash chords",
    phase: "2A",
    prerequisites: ["triad-inversions"],
  },
  {
    id: "secondary-dominant-recognition",
    track: "harmony",
    title: "Secondary dominants (V/ii, V/iii, V/IV, V/V, V/vi)",
    phase: "2A",
    prerequisites: ["cadence-recognition"],
  },
  {
    id: "modulation-recognition",
    track: "harmony",
    title: "Modulation (relative, parallel, dominant, subdominant, mediant)",
    phase: "2A",
    prerequisites: ["secondary-dominant-recognition"],
  },
  {
    id: "modes-major-family",
    track: "modes",
    title: "Modes of the major family (ionian, lydian, mixolydian)",
    phase: "2A",
    prerequisites: ["diatonic-degrees-major", "diatonic-intervals-major"],
  },
  {
    id: "modes-minor-family",
    track: "modes",
    title: "Modes of the minor family (dorian, phrygian, aeolian)",
    phase: "2A",
    prerequisites: ["modes-major-family"],
  },
  {
    id: "modes-locrian",
    track: "modes",
    title: "Locrian mode",
    phase: "2A",
    prerequisites: ["modes-minor-family"],
  },
  {
    id: "rhythmic-dictation-simple",
    track: "pitch",
    title: "Rhythmic dictation — simple meter (4/4)",
    phase: "2B+",
    prerequisites: ["diatonic-degrees-major"],
  },
  {
    id: "rhythmic-dictation-compound",
    track: "pitch",
    title: "Rhythmic dictation — compound meter (6/8)",
    phase: "2B+",
    prerequisites: ["rhythmic-dictation-simple"],
  },
  {
    id: "rhythmic-dictation-syncopation",
    track: "dictation",
    title: "Rhythmic dictation — syncopation",
    phase: "3",
    prerequisites: ["rhythmic-dictation-compound", "melodic-dictation-narrow"],
  },
  {
    id: "melodic-dictation-narrow",
    track: "dictation",
    title: "Melodic dictation — short, narrow range",
    phase: "2A",
    prerequisites: ["diatonic-degrees-major", "diatonic-intervals-major"],
  },
  {
    id: "melodic-dictation-medium",
    track: "dictation",
    title: "Melodic dictation — medium length, wider range",
    phase: "2A",
    prerequisites: ["melodic-dictation-narrow"],
  },
  {
    id: "melodic-dictation-chromatic",
    track: "dictation",
    title: "Melodic dictation — chromatic neighbors (♯4, ♭6)",
    phase: "2A",
    prerequisites: ["melodic-dictation-medium"],
  },
  // Rung 1 — shape-family nodes (PR-B redesign; fret-intervals-octaves retired).
  // Prereq chain: fret-octave-shapes → fret-intervals-standard → fret-intervals-gb-offset.
  {
    id: "fret-octave-shapes",
    track: "guitar",
    title: "Octave shapes (string-skip grips)",
    phase: "3",
    prerequisites: ["diatonic-intervals-major"],
  },
  {
    id: "fret-wide-octave-shapes",
    track: "guitar",
    title: "Octave shapes — wide span (3-string skip)",
    phase: "3",
    prerequisites: ["fret-octave-shapes"],
  },
  {
    id: "fret-skip1-intervals",
    track: "guitar",
    title: "Skip-1 interval shapes — m7 and M6 same-fret grips",
    phase: "3",
    prerequisites: ["fret-octave-shapes"],
  },
  {
    id: "fret-intervals-standard",
    track: "guitar",
    title: "Interval shapes — P4 string pairs",
    phase: "3",
    prerequisites: ["fret-octave-shapes"],
  },
  {
    id: "fret-intervals-gb-offset",
    track: "guitar",
    title: "Interval shapes — G–B pair (+1 fret offset)",
    phase: "3",
    prerequisites: ["fret-intervals-standard"],
  },
  // Melodic rung — scale degrees on the neck. The single-note counterpart to
  // the harmonic guitar rungs: hear a degree in key, tap it relative to a lit
  // tonic. Bridges the audio scale-degree track to the fretboard.
  {
    id: "fret-scale-degrees-major",
    track: "guitar",
    title: "Scale degrees on the neck (major)",
    phase: "3",
    prerequisites: ["diatonic-degrees-major", "fret-octave-shapes"],
  },
  // Minor scale type on the same melodic rung / tile as the major node above.
  // Same exercise, aeolian degrees (do re me fa sol le te) against a minor
  // cadence. Selected via the Scale-type picker, not a separate hub tile.
  {
    id: "fret-scale-degrees-minor",
    track: "guitar",
    title: "Scale degrees on the neck (minor)",
    phase: "3",
    prerequisites: ["diatonic-degrees-minor", "fret-octave-shapes"],
  },
  {
    id: "fret-triad-shapes",
    track: "guitar",
    title: "Triad shapes by string set",
    phase: "3",
    prerequisites: ["fret-intervals-standard", "triad-quality"],
  },
  {
    id: "fret-triad-inversions",
    track: "guitar",
    title: "Triad inversions across the neck",
    phase: "3",
    prerequisites: ["fret-triad-shapes", "triad-inversions"],
  },
  {
    id: "fret-caged-shapes-ea",
    track: "guitar",
    title: "CAGED shapes — E and A",
    phase: "3",
    prerequisites: ["fret-triad-inversions"],
  },
  {
    id: "fret-caged-shapes-cgd",
    track: "guitar",
    title: "CAGED shapes — C, G, and D",
    phase: "3",
    prerequisites: ["fret-caged-shapes-ea"],
  },
  {
    id: "fret-caged-anchoring",
    track: "guitar",
    title: "CAGED anchoring — shape recognition by dyad",
    phase: "3",
    prerequisites: ["fret-caged-shapes-cgd"],
  },
  // CAGED-linked melodic rung — the tonic-chord shape is lit; the user fills in
  // the non-chord scale degrees (re/fa/la/ti) inside the box. Ties each chord
  // shape to its parent scale. Prereqs: the melodic degrees + all 5 CAGED shapes.
  {
    id: "fret-scale-degrees-caged",
    track: "guitar",
    title: "Scale degrees in CAGED shapes",
    phase: "3",
    prerequisites: ["fret-scale-degrees-major", "fret-caged-shapes-cgd"],
  },
  {
    id: "fret-diatonic-triads",
    track: "guitar",
    title: "Diatonic triads on the neck",
    phase: "3",
    prerequisites: ["fret-caged-anchoring"],
  },
  {
    id: "fret-diatonic-triads-minor",
    track: "guitar",
    title: "Minor key diatonic triads on the neck",
    phase: "3",
    prerequisites: ["fret-diatonic-triads"],
  },
  {
    id: "fret-seventh-shapes",
    track: "guitar",
    title: "Seventh chord shapes on the neck",
    phase: "3",
    prerequisites: ["fret-diatonic-triads", "fret-diatonic-triads-minor"],
  },
];

/**
 * Generate all items for a given node. Each node has its own expansion logic.
 * This is where we encode "what does it mean to drill this skill?"
 */
export function expandNode(node: CurriculumNode): Item[] {
  switch (node.id) {
    case "diatonic-degrees-major": {
      // 7 items: each degree of the major scale.
      const syllables = ["do", "re", "mi", "fa", "sol", "la", "ti"];
      return Array.from({ length: 7 }, (_, i) => {
        const deg = i + 1;
        return {
          id: `${node.id}::${deg}`,
          nodeId: node.id,
          label: `Scale degree ${deg} (${syllables[i]}) in major`,
          params: { degree: deg, mode: "major" },
          exerciseType: "scale-degree-recognition",
        };
      });
    }
    case "diatonic-degrees-minor": {
      // Do-based minor — matches the generator (MINOR_SOLFEGE) and the lesson.
      const syllables = ["do", "re", "me", "fa", "sol", "le", "te"];
      return Array.from({ length: 7 }, (_, i) => {
        const deg = i + 1;
        return {
          id: `${node.id}::${deg}`,
          nodeId: node.id,
          label: `Scale degree ${deg} (${syllables[i]}) in minor`,
          params: { degree: deg, mode: "minor" },
          exerciseType: "scale-degree-recognition",
        };
      });
    }
    case "chromatic-degrees-major": {
      // 5 chromatic alterations: ♯1, ♯2, ♯4, ♭6, ♭7 (the most common in tonal music)
      const chromatics = [
        { name: "di (♯1)", deg: 1, alt: "♯" },
        { name: "ri (♯2)", deg: 2, alt: "♯" },
        { name: "fi (♯4)", deg: 4, alt: "♯" },
        { name: "le (♭6)", deg: 6, alt: "♭" },
        { name: "te (♭7)", deg: 7, alt: "♭" },
      ];
      return chromatics.map((c) => ({
        id: `${node.id}::${c.alt}${c.deg}`,
        nodeId: node.id,
        label: `Chromatic degree ${c.name} in major`,
        params: { degree: c.deg, alteration: c.alt },
        exerciseType: "scale-degree-recognition",
      }));
    }
    case "diatonic-intervals-major": {
      // Ascending and descending are separate items — descending intervals are a
      // genuinely distinct ear skill, so they carry their own card and mastery.
      // Ascending ids stay bare (`::M3`) for back-compat; descending append `::desc`.
      // Harmonic is folded into the ascending item (simultaneity not split here).
      const intervals = ["m2", "M2", "m3", "M3", "P4", "P5", "m6", "M6", "m7", "M7", "P8"];
      return intervals.flatMap((iv) => [
        {
          id: `${node.id}::${iv}`,
          nodeId: node.id,
          label: `${iv} interval ascending, major key`,
          params: { interval: iv, context: "major", direction: "ascending" },
          exerciseType: "interval-recognition" as const,
        },
        {
          id: `${node.id}::${iv}::desc`,
          nodeId: node.id,
          label: `${iv} interval descending, major key`,
          params: { interval: iv, context: "major", direction: "descending" },
          exerciseType: "interval-recognition" as const,
        },
      ]);
    }
    case "chromatic-intervals": {
      const intervals = ["A4", "d5", "A2", "d7"];
      return intervals.flatMap((iv) => [
        {
          id: `${node.id}::${iv}`,
          nodeId: node.id,
          label: `${iv} chromatic interval ascending`,
          params: { interval: iv, context: "chromatic", direction: "ascending" },
          exerciseType: "interval-recognition" as const,
        },
        {
          id: `${node.id}::${iv}::desc`,
          nodeId: node.id,
          label: `${iv} chromatic interval descending`,
          params: { interval: iv, context: "chromatic", direction: "descending" },
          exerciseType: "interval-recognition" as const,
        },
      ]);
    }
    case "triad-quality": {
      const qualities = ["maj", "min", "dim", "aug"];
      return qualities.map((q) => ({
        id: `${node.id}::${q}`,
        nodeId: node.id,
        label: `Recognize ${q} triad`,
        params: { quality: q },
        exerciseType: "chord-quality-recognition",
      }));
    }
    case "triad-inversions": {
      // 3 positions × 2 qualities (major and minor only — dim/aug inversions are rare)
      const items: Item[] = [];
      for (const q of ["maj", "min"]) {
        for (const inv of [0, 1, 2]) {
          items.push({
            id: `${node.id}::${q}::${inv}`,
            nodeId: node.id,
            label: `${q} ${["root pos", "1st inv", "2nd inv"][inv]}`,
            params: { quality: q, inversion: inv },
            exerciseType: "chord-inversion-recognition",
          });
        }
      }
      return items;
    }
    case "seventh-chord-quality": {
      const qualities = ["maj7", "min7", "dom7", "min7b5", "dim7"];
      return qualities.map((q) => ({
        id: `${node.id}::${q}`,
        nodeId: node.id,
        label: `Recognize ${q} chord`,
        params: { quality: q },
        exerciseType: "chord-quality-recognition",
      }));
    }
    case "diatonic-progressions": {
      const progs = ["I-IV-V-I", "I-V-vi-IV", "ii-V-I", "I-vi-IV-V", "vi-IV-I-V", "I-IV-I-V"];
      return progs.map((p) => ({
        id: `${node.id}::${p}`,
        nodeId: node.id,
        label: `Recognize ${p} progression`,
        params: { progression: p },
        exerciseType: "progression-recognition",
      }));
    }
    case "cadence-recognition": {
      const cadences = ["PAC", "IAC", "plagal", "half", "deceptive"];
      return cadences.map((c) => ({
        id: `${node.id}::${c}`,
        nodeId: node.id,
        label: `Recognize ${c} cadence`,
        params: { cadence: c },
        exerciseType: "cadence-recognition",
      }));
    }
    case "extended-chords-9": {
      const qualities = ["maj9", "min9", "dom9"];
      return qualities.map((q) => ({
        id: `${node.id}::${q}`,
        nodeId: node.id,
        label: `Recognize ${q} chord`,
        params: { quality: q },
        exerciseType: "chord-quality-recognition",
      }));
    }
    case "extended-chords-11-13": {
      const qualities = ["maj11", "min11", "dom13"];
      return qualities.map((q) => ({
        id: `${node.id}::${q}`,
        nodeId: node.id,
        label: `Recognize ${q} chord`,
        params: { quality: q },
        exerciseType: "chord-quality-recognition",
      }));
    }
    case "altered-dominants": {
      const qualities = ["dom7b9", "dom7s9", "dom7s11", "dom7b13"];
      return qualities.map((q) => ({
        id: `${node.id}::${q}`,
        nodeId: node.id,
        label: `Recognize ${q} chord`,
        params: { quality: q },
        exerciseType: "chord-quality-recognition",
      }));
    }
    case "slash-chords": {
      // Common slash-chord shapes from real lead sheets. Mixes inversions
      // (G/B, F/C, C/E) with non-chord-tone basses (F/G) — slash notation
      // covers both, and the user is identifying by sound + name, not
      // grading inversion arithmetic (that's the inversion node's job).
      const slashes: Array<{ upperRoot: string; upperQuality: string; bass: string }> = [
        { upperRoot: "G", upperQuality: "maj", bass: "B" },
        { upperRoot: "F", upperQuality: "maj", bass: "C" },
        { upperRoot: "C", upperQuality: "maj", bass: "E" },
        { upperRoot: "F", upperQuality: "maj", bass: "G" },
      ];
      return slashes.map((s) => ({
        id: `${node.id}::${s.upperRoot}-over-${s.bass}`,
        nodeId: node.id,
        label: `${s.upperRoot}/${s.bass} slash chord`,
        params: { upperRoot: s.upperRoot, upperQuality: s.upperQuality, bass: s.bass },
        exerciseType: "slash-chord-recognition",
      }));
    }
    case "secondary-dominant-recognition": {
      // Five non-tonic non-diminished diatonic degrees in major: ii, iii, IV, V, vi.
      const targets: Array<{ deg: number; label: string }> = [
        { deg: 2, label: "V/ii" },
        { deg: 3, label: "V/iii" },
        { deg: 4, label: "V/IV" },
        { deg: 5, label: "V/V" },
        { deg: 6, label: "V/vi" },
      ];
      return targets.map((t) => ({
        id: `${node.id}::${t.label}`,
        nodeId: node.id,
        label: `Recognize ${t.label} tonicization`,
        params: { target: t.deg },
        exerciseType: "secondary-dominant-recognition",
      }));
    }
    case "modulation-recognition": {
      const rels = ["relative", "parallel", "dominant", "subdominant", "mediant"] as const;
      return rels.map((r) => ({
        id: `${node.id}::${r}`,
        nodeId: node.id,
        label: `Recognize ${r} modulation`,
        params: { relationship: r },
        exerciseType: "modulation-recognition",
      }));
    }
    case "modes-major-family": {
      const modes = ["ionian", "lydian", "mixolydian"] as const;
      return modes.map((m) => ({
        id: `${node.id}::${m}`,
        nodeId: node.id,
        label: `Recognize ${m} mode`,
        params: { mode: m },
        exerciseType: "mode-recognition",
      }));
    }
    case "modes-minor-family": {
      const modes = ["dorian", "phrygian", "aeolian"] as const;
      return modes.map((m) => ({
        id: `${node.id}::${m}`,
        nodeId: node.id,
        label: `Recognize ${m} mode`,
        params: { mode: m },
        exerciseType: "mode-recognition",
      }));
    }
    case "modes-locrian": {
      return [
        {
          id: `${node.id}::locrian`,
          nodeId: node.id,
          label: "Recognize locrian mode",
          params: { mode: "locrian" },
          exerciseType: "mode-recognition",
        },
      ];
    }
    case "rhythmic-dictation-simple": {
      const figures: Array<{ suffix: string; pattern: string; figure: string }> = [
        { suffix: "even-quarters",     pattern: "10101010", figure: "Even quarters" },
        { suffix: "div-beat1",         pattern: "11101010", figure: "Beat 1 divided" },
        { suffix: "div-beat2",         pattern: "10111010", figure: "Beat 2 divided" },
        { suffix: "div-beat4",         pattern: "10101011", figure: "Beat 4 divided" },
        { suffix: "dotted-beat1",      pattern: "10011010", figure: "Dotted figure, beat 1" },
        { suffix: "half-quarters",     pattern: "10001010", figure: "Half + two quarters" },
        { suffix: "all-eighths",       pattern: "11111111", figure: "All eighths" },
        { suffix: "rest-beat1",        pattern: "00101010", figure: "Rest on beat 1" },
      ];
      return figures.map((f) => ({
        id: `${node.id}::${f.suffix}`,
        nodeId: node.id,
        label: `Rhythmic figure: ${f.figure}`,
        params: { pattern: f.pattern, meter: "4/4", figure: f.figure },
        exerciseType: "rhythmic-dictation" as const,
      }));
    }
    case "rhythmic-dictation-compound": {
      const figures: Array<{ suffix: string; pattern: string; figure: string }> = [
        { suffix: "even-dotted",       pattern: "100100", figure: "Even dotted quarters" },
        { suffix: "div-beat1",         pattern: "111100", figure: "Beat 1 divided" },
        { suffix: "div-beat2",         pattern: "100111", figure: "Beat 2 divided" },
        { suffix: "all-eighths",       pattern: "111111", figure: "All eighths" },
        { suffix: "dotted-8th-16th",   pattern: "101100", figure: "Dotted eighth + 16th" },
        { suffix: "rest-beat1",        pattern: "000100", figure: "Rest on beat 1" },
        { suffix: "eighth-quarter",    pattern: "110100", figure: "Eighth + quarter" },
        { suffix: "hemiola",           pattern: "100010", figure: "Hemiola cross-accent" },
      ];
      return figures.map((f) => ({
        id: `${node.id}::${f.suffix}`,
        nodeId: node.id,
        label: `Rhythmic figure (6/8): ${f.figure}`,
        params: { pattern: f.pattern, meter: "6/8", figure: f.figure },
        exerciseType: "rhythmic-dictation" as const,
      }));
    }
    case "rhythmic-dictation-syncopation": {
      const figures: Array<{ suffix: string; pattern: string; figure: string }> = [
        { suffix: "synco-and1",        pattern: "01010100", figure: "Syncopation: and-of-1" },
        { suffix: "synco-and2",        pattern: "10011010", figure: "Syncopation: and-of-2" },
        { suffix: "synco-and3",        pattern: "10100110", figure: "Syncopation: and-of-3" },
        { suffix: "offbeat-pairs",     pattern: "01010101", figure: "Off-beat pairs" },
        { suffix: "quarter-tied",      pattern: "10110000", figure: "Quarter + tied eighths" },
        { suffix: "backbeat",          pattern: "00100010", figure: "Backbeat" },
        { suffix: "pickup-and4",       pattern: "10101001", figure: "And-of-4 pickup" },
        { suffix: "clave",             pattern: "10010100", figure: "Short clave phrase" },
      ];
      return figures.map((f) => ({
        id: `${node.id}::${f.suffix}`,
        nodeId: node.id,
        label: `Syncopated figure: ${f.figure}`,
        params: { pattern: f.pattern, meter: "4/4", figure: f.figure },
        exerciseType: "rhythmic-dictation" as const,
      }));
    }
    case "melodic-dictation-narrow": {
      // Five 3-note diatonic dictation prompts at a narrow range (≤ 5th).
      // Each item has a distinct seed (encoded in the id) so the same
      // user keeps revisiting the same five melodies — FSRS scheduling
      // is per-item, not per-instance, and item identity is the key.
      // Length is 3 across the board for the entry-level node, per the
      // spec §8.5 "3-note diatonic, range ≤ 5th."
      return [1, 2, 3, 4, 5].map((n) => ({
        id: `${node.id}::${n}`,
        nodeId: node.id,
        label: `Short melodic dictation #${n}`,
        params: { length: 3, range: "narrow", chromatic: 0 },
        exerciseType: "melodic-dictation",
      }));
    }
    case "melodic-dictation-medium": {
      // Five 4–5-note prompts at a wider range. Two items at length 4,
      // three at length 5 — mixes the spec's "4-5 notes, full diatonic"
      // language without forcing identical-length grinding.
      const sizes = [4, 4, 5, 5, 5];
      return sizes.map((len, i) => ({
        id: `${node.id}::${i + 1}`,
        nodeId: node.id,
        label: `Melodic dictation #${i + 1} (${len} notes)`,
        params: { length: len, range: "wide", chromatic: 0 },
        exerciseType: "melodic-dictation",
      }));
    }
    case "melodic-dictation-chromatic": {
      // Five chromatic prompts — same length distribution as -medium
      // so the difficulty bump is purely the chromatic neighbors. The
      // generator's chromatic mode samples ♯4 / ♭6 with ~25% chance,
      // so most prompts will surface at least one alteration across
      // the set.
      const sizes = [4, 4, 5, 5, 5];
      return sizes.map((len, i) => ({
        id: `${node.id}::${i + 1}`,
        nodeId: node.id,
        label: `Chromatic melodic dictation #${i + 1}`,
        params: { length: len, range: "wide", chromatic: 1 },
        exerciseType: "melodic-dictation",
      }));
    }
    case "fret-octave-shapes": {
      // 4 items — one per canonical skip-1-string octave grip.
      // The generator randomises the root fret; the shape (pair + offset) is fixed.
      // See docs/design/guitar-harmony-v2.md §4.1 and theory-engine/fretboard.ts OCTAVE_SHAPES.
      const shapes = [
        { symbol: "oct-ed", label: "E–D string-skip octave (6–4)" },
        { symbol: "oct-ag", label: "A–G string-skip octave (5–3)" },
        { symbol: "oct-db", label: "D–B string-skip octave (4–2)" },
        { symbol: "oct-ge", label: "G–e string-skip octave (3–1)" },
      ];
      return shapes.map((s) => ({
        id: `${node.id}::${s.symbol}`,
        nodeId: node.id,
        label: s.label,
        params: { interval: "P8", shapeFamily: "octave", octaveShapeSymbol: s.symbol },
        exerciseType: "fret-interval-recognition",
      }));
    }
    case "fret-wide-octave-shapes": {
      // 3 items — skip-2-string (wide) octave grips; upper note sits 2–3 frets
      // back toward the nut (negative fretOffset). See theory-engine/fretboard.ts WIDE_OCTAVE_SHAPES.
      const shapes = [
        { symbol: "wide-eg", label: "E–G wide octave (6–3)" },
        { symbol: "wide-ab", label: "A–B wide octave (5–2)" },
        { symbol: "wide-de", label: "D–e wide octave (4–1)" },
      ];
      return shapes.map((s) => ({
        id: `${node.id}::${s.symbol}`,
        nodeId: node.id,
        label: s.label,
        params: { interval: "P8", shapeFamily: "wide-octave", wideOctaveShapeSymbol: s.symbol },
        exerciseType: "fret-interval-recognition",
      }));
    }
    case "fret-skip1-intervals": {
      // 4 items — same-fret skip-1-string grips: m7 on E–D/A–G pairs (open Δ = 10),
      // M6 on D–B/G–e pairs (open Δ = 9). Both land at fretOffset = 0.
      // See theory-engine/fretboard.ts SKIP1_INTERVAL_SHAPES.
      const shapes = [
        { symbol: "skip1-m7-ed", label: "m7 · E–D string-skip (6–4)" },
        { symbol: "skip1-m7-ag", label: "m7 · A–G string-skip (5–3)" },
        { symbol: "skip1-m6-db", label: "M6 · D–B string-skip (4–2)" },
        { symbol: "skip1-m6-ge", label: "M6 · G–e string-skip (3–1)" },
      ];
      return shapes.map((s) => ({
        id: `${node.id}::${s.symbol}`,
        nodeId: node.id,
        label: s.label,
        params: {
          interval: s.symbol.includes("m7") ? "m7" : "M6",
          shapeFamily: "skip1-interval",
          skip1IntervalSymbol: s.symbol,
        },
        exerciseType: "fret-interval-recognition",
      }));
    }
    case "fret-intervals-standard": {
      // 7 items — one per interval shape across the four P4 string pairs.
      // One card trains one transferable motor pattern; the generator randomises
      // which P4 pair and which fret each round.
      const intervals: Array<{ symbol: string; name: string }> = [
        { symbol: "P5", name: "perfect 5th" },
        { symbol: "P4", name: "perfect 4th" },
        { symbol: "M3", name: "major 3rd" },
        { symbol: "m3", name: "minor 3rd" },
        { symbol: "M6", name: "major 6th" },
        { symbol: "m6", name: "minor 6th" },
        { symbol: "M2", name: "major 2nd" },
      ];
      return intervals.map((iv) => ({
        id: `${node.id}::${iv.symbol}`,
        nodeId: node.id,
        label: `${iv.name} shape — P4 string pairs`,
        params: { interval: iv.symbol, shapeFamily: "standard" },
        exerciseType: "fret-interval-recognition",
      }));
    }
    case "fret-intervals-gb-offset": {
      // 7 items — same shapes as fret-intervals-standard but on the G–B pair,
      // where every shape stretches +1 fret (M3 tuning instead of P4).
      const intervals: Array<{ symbol: string; name: string }> = [
        { symbol: "P5", name: "perfect 5th" },
        { symbol: "P4", name: "perfect 4th" },
        { symbol: "M3", name: "major 3rd" },
        { symbol: "m3", name: "minor 3rd" },
        { symbol: "M6", name: "major 6th" },
        { symbol: "m6", name: "minor 6th" },
        { symbol: "M2", name: "major 2nd" },
      ];
      return intervals.map((iv) => ({
        id: `${node.id}::${iv.symbol}`,
        nodeId: node.id,
        label: `${iv.name} shape — G–B pair (+1 fret offset)`,
        params: { interval: iv.symbol, shapeFamily: "gb-offset" },
        exerciseType: "fret-interval-recognition",
      }));
    }
    case "fret-scale-degrees-major": {
      // 7 items — one per major-scale degree. The generator randomises the key
      // and the tonic anchor position; the degree is the item identity.
      const syllables = ["do", "re", "mi", "fa", "sol", "la", "ti"];
      return Array.from({ length: 7 }, (_, i) => {
        const deg = i + 1;
        return {
          id: `${node.id}::${deg}`,
          nodeId: node.id,
          label: `Scale degree ${deg} (${syllables[i]}) on the neck`,
          params: { degree: deg, mode: "major" },
          exerciseType: "fret-scale-degree-recognition",
        };
      });
    }
    case "fret-scale-degrees-minor": {
      // 7 items — one per (natural) minor-scale degree. Same rung as the major
      // node; aeolian solfège and a minor cadence. Octaves is a session knob,
      // not part of the item identity.
      const syllables = ["do", "re", "me", "fa", "sol", "le", "te"];
      return Array.from({ length: 7 }, (_, i) => {
        const deg = i + 1;
        return {
          id: `${node.id}::${deg}`,
          nodeId: node.id,
          label: `Scale degree ${deg} (${syllables[i]}) on the neck — minor`,
          params: { degree: deg, mode: "minor" },
          exerciseType: "fret-scale-degree-recognition",
        };
      });
    }
    case "fret-scale-degrees-caged": {
      // 20 items: the 4 non-chord scale degrees (re/fa/la/ti) × all 5 CAGED
      // shapes. The lit tonic-chord shape supplies do/mi/sol; the user finds the
      // degrees that fill in the scale inside the box.
      const SHAPES = ["E", "A", "G", "C", "D"] as const;
      const DEGREES: Array<{ degree: number; syllable: string }> = [
        { degree: 2, syllable: "re" },
        { degree: 4, syllable: "fa" },
        { degree: 6, syllable: "la" },
        { degree: 7, syllable: "ti" },
      ];
      return SHAPES.flatMap((shape) =>
        DEGREES.map(({ degree, syllable }) => ({
          id: `${node.id}::${shape}::${degree}`,
          nodeId: node.id,
          label: `${syllable} inside the ${shape} shape`,
          params: { shape, degree },
          exerciseType: "fret-scale-degree-caged-recognition",
        })),
      );
    }
    case "fret-triad-shapes": {
      // 16 items: all 4 qualities × all 4 string sets. Each string set
      // produces genuinely different fingering shapes due to the G-B +1 fret
      // offset, so all sets need dedicated items. `stringSet` indexes
      // STRING_SETS (top-set-first). Root position throughout (inversions node 3).
      const QUALITIES_ALL = [
        { quality: "maj", name: "major" },
        { quality: "min", name: "minor" },
        { quality: "dim", name: "diminished" },
        { quality: "aug", name: "augmented" },
      ];
      const SET_NAMES = ["e–B–G", "B–G–D", "G–D–A", "D–A–E"];
      return [0, 1, 2, 3].flatMap((stringSet) =>
        QUALITIES_ALL.map((q) => ({
          id: `${node.id}::${q.quality}::set${stringSet}`,
          nodeId: node.id,
          label: `Build the ${q.name} triad (${SET_NAMES[stringSet]})`,
          params: { quality: q.quality, stringSet, inversion: 0 },
          exerciseType: "fret-triad-recognition",
        })),
      );
    }
    case "fret-triad-inversions": {
      // 24 items: maj/min × 3 inversions × 4 string sets. Set-0 IDs have no
      // set suffix (stable from v1); sets 1-3 append ::set<n> to the ID.
      const invNames = ["root position", "1st inversion", "2nd inversion"];
      const SET_NAMES = ["e–B–G", "B–G–D", "G–D–A", "D–A–E"];
      const qualities: Array<{ quality: string; name: string }> = [
        { quality: "maj", name: "major" },
        { quality: "min", name: "minor" },
      ];
      return [0, 1, 2, 3].flatMap((stringSet) =>
        qualities.flatMap((q) =>
          [0, 1, 2].map((inv) => ({
            id: stringSet === 0
              ? `${node.id}::${q.quality}::${inv}`
              : `${node.id}::${q.quality}::${inv}::set${stringSet}`,
            nodeId: node.id,
            label: `${q.name} triad — ${invNames[inv]} (${SET_NAMES[stringSet]})`,
            params: { quality: q.quality, inversion: inv, stringSet },
            exerciseType: "fret-triad-inversion-recognition",
          })),
        ),
      );
    }
    case "fret-caged-shapes-ea": {
      // 4 items: E and A shapes, major and minor.
      const items = [
        { shape: "E", quality: "maj", label: "E-shape major (root on low E string)" },
        { shape: "E", quality: "min", label: "E-shape minor (root on low E string)" },
        { shape: "A", quality: "maj", label: "A-shape major (root on A string)" },
        { shape: "A", quality: "min", label: "A-shape minor (root on A string)" },
      ] as const;
      return items.map((s) => ({
        id: `${node.id}::${s.shape}::${s.quality}`,
        nodeId: node.id,
        label: s.label,
        params: { shape: s.shape, quality: s.quality },
        exerciseType: "fret-caged-recognition" as const,
      }));
    }
    case "fret-caged-shapes-cgd": {
      // 6 items: C, G, D shapes, major and minor.
      const items = [
        { shape: "C", quality: "maj", label: "C-shape major (root on B string)" },
        { shape: "C", quality: "min", label: "C-shape minor (root on B string)" },
        { shape: "G", quality: "maj", label: "G-shape major (root on low E string)" },
        { shape: "G", quality: "min", label: "G-shape minor (root on low E string)" },
        { shape: "D", quality: "maj", label: "D-shape major (root on B string)" },
        { shape: "D", quality: "min", label: "D-shape minor (root on B string)" },
      ] as const;
      return items.map((s) => ({
        id: `${node.id}::${s.shape}::${s.quality}`,
        nodeId: node.id,
        label: s.label,
        params: { shape: s.shape, quality: s.quality },
        exerciseType: "fret-caged-recognition" as const,
      }));
    }
    case "fret-caged-anchoring": {
      // 10 items: all 5 CAGED shapes, major and minor. No shape name given
      // in the prompt — the user must recognise the shape from the dyad alone.
      const items = [
        { shape: "E", quality: "maj", label: "E-shape major — anchor by dyad" },
        { shape: "E", quality: "min", label: "E-shape minor — anchor by dyad" },
        { shape: "A", quality: "maj", label: "A-shape major — anchor by dyad" },
        { shape: "A", quality: "min", label: "A-shape minor — anchor by dyad" },
        { shape: "G", quality: "maj", label: "G-shape major — anchor by dyad" },
        { shape: "G", quality: "min", label: "G-shape minor — anchor by dyad" },
        { shape: "C", quality: "maj", label: "C-shape major — anchor by dyad" },
        { shape: "C", quality: "min", label: "C-shape minor — anchor by dyad" },
        { shape: "D", quality: "maj", label: "D-shape major — anchor by dyad" },
        { shape: "D", quality: "min", label: "D-shape minor — anchor by dyad" },
      ] as const;
      return items.map((s) => ({
        id: `${node.id}::${s.shape}::${s.quality}`,
        nodeId: node.id,
        label: s.label,
        params: { shape: s.shape, quality: s.quality },
        exerciseType: "fret-caged-anchoring" as const,
      }));
    }
    case "fret-diatonic-triads": {
      // 25 items: 5 diatonic degrees × 5 CAGED shapes (all major key).
      const DEGREES = ["I", "ii", "IV", "V", "vi"] as const;
      const SHAPES  = ["E", "A", "G", "C", "D"] as const;
      const QUALITY: Record<typeof DEGREES[number], "maj" | "min"> = {
        I: "maj", ii: "min", IV: "maj", V: "maj", vi: "min",
      };
      const items: Item[] = [];
      for (const degree of DEGREES) {
        for (const shape of SHAPES) {
          items.push({
            id: `${node.id}::${degree}::${shape}`,
            nodeId: node.id,
            label: `Degree ${degree} in ${shape}-shape (${QUALITY[degree]})`,
            params: { shape, degree },
            exerciseType: "fret-diatonic-recognition" as const,
          });
        }
      }
      return items;
    }
    case "fret-diatonic-triads-minor": {
      // 25 items: 5 minor-key diatonic degrees × 5 CAGED shapes.
      const DEGREES = ["i", "III", "iv", "V", "VII"] as const;
      const SHAPES  = ["E", "A", "G", "C", "D"] as const;
      const QUALITY: Record<typeof DEGREES[number], "maj" | "min"> = {
        i: "min", III: "maj", iv: "min", V: "maj", VII: "maj",
      };
      const items: Item[] = [];
      for (const degree of DEGREES) {
        for (const shape of SHAPES) {
          items.push({
            id: `${node.id}::${degree}::${shape}`,
            nodeId: node.id,
            label: `Minor degree ${degree} in ${shape}-shape (${QUALITY[degree]})`,
            params: { shape, degree },
            exerciseType: "fret-diatonic-minor-recognition" as const,
          });
        }
      }
      return items;
    }
    case "fret-seventh-shapes": {
      // 15 items: 3 seventh-chord qualities × 5 CAGED shapes.
      const QUALITIES = ["maj7", "min7", "dom7"] as const;
      const SHAPES    = ["E", "A", "G", "C", "D"] as const;
      const items: Item[] = [];
      for (const quality of QUALITIES) {
        for (const shape of SHAPES) {
          items.push({
            id: `${node.id}::${quality}::${shape}`,
            nodeId: node.id,
            label: `${quality} chord in ${shape}-shape`,
            params: { shape, quality },
            exerciseType: "fret-seventh-recognition" as const,
          });
        }
      }
      return items;
    }
    default:
      return [];
  }
}

/** Expand the entire MVP curriculum into a flat item list. */
export function buildAllItems(nodes: CurriculumNode[] = MVP_NODES): Item[] {
  return nodes.flatMap(expandNode);
}

/**
 * The nodes a user with the given opt-ins should see. Drops nodes on
 * instrument-specific tracks the user hasn't enabled. Ear-training nodes
 * always pass through. This is the gate seam for the daily plan, drill hub,
 * progress, and placement — call it once on `MVP_NODES` and thread the result.
 */
export function visibleNodes(
  nodes: CurriculumNode[],
  enabledInstrumentTracks: readonly Track[],
): CurriculumNode[] {
  return nodes.filter((n) => isTrackEnabled(n.track, enabledInstrumentTracks));
}

/**
 * The items a user with the given opt-ins should see. Drops items whose node
 * is on a dormant instrument track. Filtering items keeps dormant content out
 * of scheduling without touching persisted card state — disabling a track
 * simply stops its cards from being scheduled, it doesn't delete them.
 */
export function visibleItems(
  items: Item[],
  enabledInstrumentTracks: readonly Track[],
  nodes: CurriculumNode[] = MVP_NODES,
): Item[] {
  const trackOf = new Map(nodes.map((n) => [n.id, n.track]));
  return items.filter((i) => {
    const track = trackOf.get(i.nodeId);
    return track === undefined || isTrackEnabled(track, enabledInstrumentTracks);
  });
}

/** Look up the node a given item belongs to. */
export function nodeOf(item: Item, nodes: CurriculumNode[] = MVP_NODES): CurriculumNode | undefined {
  return nodes.find((n) => n.id === item.nodeId);
}

/**
 * Whether a card id is an abandoned orphan — a card written by an old free-drill
 * mapping that no longer exists, and which the mastery rollup ignores. These are
 * safe to delete: they were already invisible to node/track mastery and are never
 * scheduled (the session builder is items-driven).
 *
 * Scope is deliberately narrow — only the `exercise-*::` namespace (descending /
 * abstract intervals, short-context cadences, all-inversion chords, drill-only
 * progressions, all since re-homed onto real curriculum items) and the three
 * phantom `rhythmic-dictation-*::1` ids (the real figures use suffixes like
 * `::even-quarters`). It must NOT match legitimate drill-only practice cards
 * (`drill-blues::*`, `drill-harmonic-minor::*`) or real curriculum items such as
 * `melodic-dictation-narrow::1`.
 */
export function isAbandonedCardId(itemId: string): boolean {
  if (itemId.startsWith("exercise-")) return true;
  return (
    itemId === "rhythmic-dictation-simple::1" ||
    itemId === "rhythmic-dictation-compound::1" ||
    itemId === "rhythmic-dictation-syncopation::1"
  );
}

/**
 * Return the track for an item given its stable ID.
 * Item IDs follow the pattern `"<nodeId>::<suffix>"`. Returns null when
 * the nodeId is not found in `nodes` (e.g. drill-only items).
 */
export function getItemTrack(itemId: string, nodes: CurriculumNode[] = MVP_NODES): Track | null {
  const nodeId = itemId.includes("::") ? itemId.split("::")[0] : itemId;
  const node = nodes.find((n) => n.id === nodeId);
  return node?.track ?? null;
}
